/**
 * Resource Orders Tool
 *
 * Tool implementation for Promoter Resource Orders tracking
 */

import { FaShoppingCart } from "react-icons/fa";
import { BaseTool } from "../BaseTool";
import { ordersApi, dashboardApi } from "../../services/toolsApi";

export class ResourceOrdersTool extends BaseTool {
  constructor() {
    super();

    // Metadata
    this.id = "orders";
    this.name = "Resource Orders";
    this.description = "Promoter resource order management and tracking";
    this.icon = FaShoppingCart;
    this.color = "#E97677";
    this.path = "/dashboard/tools/promoters?tab=orders";
    this.navHidden = true; // reached via the Promoters tool's Resource Orders tab

    // Collections
    this.collections = ["resource_orders"];

    // Access control
    this.requiresAdmin = false;

    // Summary configuration
    this.summaryConfig = {
      enabled: true,
      mainStat: "pending",
      expectedStatCount: 4, // pending, processing, completed, total
    };

    // Filter configuration
    this.filterConfig = {
      search: {
        enabled: true,
        placeholder: "Search orders or companies...",
        fields: ["orderId", "companyName", "referralKey", "resources"],
      },
      buttonGroups: [
        {
          label: "Status",
          key: "statusFilter",
          defaultValue: "all",
          options: [
            { value: "all", label: "All Orders", icon: "FaBox" },
            { value: "pending", label: "Pending", icon: "FaClock" },
            { value: "processing", label: "Processing", icon: "FaTruck" },
            { value: "completed", label: "Completed", icon: "FaCheckCircle" },
          ],
        },
      ],
      sortOptions: [
        { value: "createdAt", label: "Newest First" },
        { value: "status", label: "Status" },
        { value: "company", label: "Company Name" },
      ],
      defaultSort: "createdAt",
    };
  }

  /**
   * Get summary data for dashboard widget
   */
  async getSummaryData() {
    try {
      // dashboardApi.getResourceOrdersSummary() returns { summary, lastUpdated }.
      // (ordersApi has no getSummary() — calling it threw, so this dashboard
      // widget previously always errored.)
      const response = await dashboardApi.getResourceOrdersSummary();

      if (!response || !response.summary) {
        throw new Error("Invalid response from getResourceOrdersSummary");
      }

      return {
        success: true,
        data: {
          pending: response.summary.pending || 0,
          processing: response.summary.processing || 0,
          completed: response.summary.completed || 0,
          total: response.summary.total || 0,
        },
        lastUpdated: response.lastUpdated
          ? new Date(response.lastUpdated)
          : new Date(),
      };
    } catch (error) {
      console.error("Error fetching orders summary:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get detailed stats with filters
   */
  async getDetailedStats(filters = {}) {
    try {
      const { status = "all" } = filters;
      const data = await ordersApi.getAllOrders({ status });

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Custom backup preparation
   * Could exclude cancelled orders or include order history
   */
  async prepareBackup(collections, format) {
    // Could add logic to:
    // - Include order_history for completed orders
    // - Exclude cancelled orders
    // - Flatten nested resource arrays for CSV

    if (format === "csv") {
      // Future: Custom CSV formatting for orders
    }

    return collections;
  }
}

export default ResourceOrdersTool;
